<template>
  <div class="mini-game-resources-container">
    <div class="header-actions">
      <el-button type="primary" icon="el-icon-plus" @click="handleCreate">新增资源配置</el-button>
    </div>

    <el-table :data="list" border style="width: 100%" v-loading="loading">
      <el-table-column prop="id" label="ID" width="80" align="center" />
      <el-table-column prop="name" label="资源名" min-width="150" />
      <el-table-column prop="res_type" label="类型" width="120" align="center" />
      <el-table-column label="音频" width="120" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.sound_url" type="success" size="small">有</el-tag>
          <span v-else style="color: #909399">无</span>
        </template>
      </el-table-column>
      <el-table-column label="资源配置摘要" min-width="300">
        <template slot-scope="scope">
          <div v-if="scope.row.config && scope.row.config.length">
            <el-tag 
              v-for="(item, idx) in scope.row.config" 
              :key="idx" 
              size="mini" 
              style="margin-right: 5px; margin-bottom: 5px;"
            >
              <span v-if="scope.row.res_type === '自旋转'">旋转序列</span>
              <span v-else>{{ item.angle }}°</span>
              ({{ (item.images || []).length }}张)
            </el-tag>
          </div>
          <span v-else style="color: #909399">暂无配置</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="180" align="center">
        <template slot-scope="scope">
          <el-button size="mini" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Edit Dialog -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="800px" :close-on-click-modal="false">
      <el-form :model="form" label-width="100px">
        <el-form-item label="资源名" required>
          <el-input v-model="form.name" placeholder="请输入资源名称" />
        </el-form-item>
        
        <el-form-item label="资源类型">
          <el-select v-model="form.res_type" placeholder="请选择资源类型" style="width: 100%">
            <el-option label="角度" value="角度" />
            <el-option label="自旋转" value="自旋转" />
          </el-select>
        </el-form-item>
        
        <div class="resource-items-header">
          <span>资源项配置</span>
          <el-button type="text" icon="el-icon-plus" @click="addItem" v-if="form.res_type === '角度' || form.config.length === 0">添加项</el-button>
        </div>

        <div v-for="(item, index) in form.config" :key="index" class="resource-item-box" @paste="handlePaste($event, index)">
          <div class="item-header">
            <span class="item-title">项 #{{ index + 1 }}</span>
            <el-button type="text" style="color: #F56C6C" icon="el-icon-delete" @click="removeItem(index)">删除</el-button>
          </div>
          
          <el-form-item label="角度 (0-359)" v-if="form.res_type === '角度'">
            <el-input-number v-model="item.angle" :min="0" :max="359" style="width: 100%" />
          </el-form-item>
          
          <el-form-item :label="form.res_type === '自旋转' ? '旋转序列' : '图片'">
            <div class="image-list">
              <div v-for="(img, imgIdx) in item.images" :key="imgIdx" class="image-item">
                <el-image 
                  :src="resolveMediaUrl(img)" 
                  fit="cover" 
                  class="preview-img" 
                  :preview-src-list="item.images.map(url => resolveMediaUrl(url))" 
                />
                <div class="image-actions">
                  <i class="el-icon-delete" @click="removeImage(index, imgIdx)"></i>
                </div>
              </div>
              <el-upload
                action=""
                multiple
                accept="image/*"
                :show-file-list="false"
                :http-request="(opts) => uploadImage(opts, index)"
                class="image-uploader"
              >
                <i class="el-icon-plus uploader-icon"></i>
              </el-upload>
            </div>
          </el-form-item>
        </div>

        <el-divider></el-divider>

        <el-form-item label="音频设置">
          <div class="audio-setting">
            <div v-if="form.sound_url" class="audio-item">
              <audio :src="resolveMediaUrl(form.sound_url)" controls style="height: 40px; max-width: 300px;"></audio>
              <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeAudio" style="margin-left: 10px;">删除</el-button>
            </div>
            <el-upload
              v-else
              action=""
              accept="audio/*"
              :show-file-list="false"
              :http-request="uploadAudio"
              class="audio-uploader"
            >
              <el-button type="primary" icon="el-icon-upload">上传音频</el-button>
              <span style="color: #909399; font-size: 12px; margin-left: 8px;">支持 mp3/wav/ogg 等格式</span>
            </el-upload>
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getMiniGameResources,
  createMiniGameResource,
  updateMiniGameResource,
  deleteMiniGameResource,
  uploadStoryImage,
  uploadMiniGameAudio
} from "@/net/api";
import { resolveMediaUrl } from "@/utils/media";

export default {
  name: "MiniGameResources",
  data() {
    return {
      list: [],
      loading: false,
      dialogVisible: false,
      dialogTitle: "新增资源配置",
      submitting: false,
      uploadingCount: 0,
      form: {
        id: null,
        name: "",
        res_type: "角度",
        config: [],
        sound_url: ""
      }
    };
  },
  created() {
    this.fetchList();
  },
  methods: {
    resolveMediaUrl,
    async fetchList() {
      this.loading = true;
      try {
        const { data } = await getMiniGameResources();
        this.list = data;
      } catch (e) {
        this.$message.error("获取列表失败");
      } finally {
        this.loading = false;
      }
    },
    handleCreate() {
      this.dialogTitle = "新增资源配置";
      this.form = {
        id: null,
        name: "",
        res_type: "角度",
        config: [],
        sound_url: ""
      };
      this.dialogVisible = true;
    },
    handleEdit(row) {
      this.dialogTitle = "编辑资源配置";
      this.form = {
        id: row.id,
        name: row.name,
        res_type: row.res_type || "角度",
        config: JSON.parse(JSON.stringify(row.config || [])),
        sound_url: row.sound_url || ""
      };
      this.dialogVisible = true;
    },
    async handleDelete(row) {
      try {
        await this.$confirm("确定删除该资源配置吗？", "提示", { type: "warning" });
        await deleteMiniGameResource(row.id);
        this.$message.success("删除成功");
        this.fetchList();
      } catch (e) {
        if (e !== "cancel") {
          this.$message.error("删除失败");
        }
      }
    },
    addItem() {
      this.form.config.push({
        angle: 0,
        images: []
      });
    },
    removeItem(index) {
      this.form.config.splice(index, 1);
    },
    async uploadImage(options, configIndex) {
      const imagesArray = this.form.config[configIndex].images;
      const localUrl = URL.createObjectURL(options.file);
      imagesArray.push(localUrl);
      this.uploadingCount++;
      
      try {
        const { data } = await uploadStoryImage(options.file);
        const index = imagesArray.indexOf(localUrl);
        if (index !== -1) {
          this.$set(imagesArray, index, data.url);
        }
      } catch (e) {
        console.error("Upload error:", e);
        this.$message.error("上传图片失败");
        const index = imagesArray.indexOf(localUrl);
        if (index !== -1) {
          imagesArray.splice(index, 1);
        }
      } finally {
        this.uploadingCount--;
      }
    },
    handlePaste(event, index) {
      const items = (event.clipboardData || window.clipboardData).items;
      const files = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
      
      files.sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        return nameA.localeCompare(nameB, undefined, { numeric: true });
      });

      for (const file of files) {
        this.uploadImage({ file }, index);
      }
    },
    removeImage(configIndex, imgIndex) {
      this.form.config[configIndex].images.splice(imgIndex, 1);
    },
    async uploadAudio(options) {
      try {
        const { data } = await uploadMiniGameAudio(options.file);
        this.form.sound_url = data.url;
        this.$message.success("音频上传成功");
      } catch (e) {
        console.error("Audio upload error:", e);
        this.$message.error("上传音频失败");
      }
    },
    removeAudio() {
      this.form.sound_url = "";
    },
    async submitForm() {
      if (this.uploadingCount > 0) {
        this.$message.warning("请等待所有图片上传完成");
        return;
      }
      if (!this.form.name) {
        this.$message.warning("请输入资源名");
        return;
      }
      this.submitting = true;
      try {
        const payload = {
          name: this.form.name,
          res_type: this.form.res_type,
          config: this.form.config,
          sound_url: this.form.sound_url
        };
        if (this.form.id) {
          await updateMiniGameResource(this.form.id, payload);
          this.$message.success("修改成功");
        } else {
          await createMiniGameResource(payload);
          this.$message.success("创建成功");
        }
        this.dialogVisible = false;
        this.fetchList();
      } catch (e) {
        this.$message.error("提交失败");
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.mini-game-resources-container {
  padding: 20px;
}
.header-actions {
  margin-bottom: 20px;
}
.resource-items-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid #EBEEF5;
  font-weight: bold;
}
.resource-item-box {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 15px;
  background-color: #F9FAFC;
}
.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.item-title {
  font-size: 14px;
  color: #606266;
  font-weight: bold;
}
.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.image-item {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #DCDFE6;
}
.preview-img {
  width: 100%;
  height: 100%;
}
.image-actions {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  pointer-events: none;
}
.image-item:hover .image-actions {
  opacity: 1;
}
.image-actions i {
  pointer-events: auto;
}
.image-uploader {
  width: 100px;
  height: 100px;
  border: 1px dashed #DCDFE6;
  border-radius: 4px;
  cursor: pointer;
  transition: border-color 0.3s;
}
.image-uploader:hover {
  border-color: #409EFF;
}
.image-uploader /deep/ .el-upload {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.uploader-icon {
  font-size: 28px;
  color: #8c939d;
}
.audio-setting {
  display: flex;
  align-items: center;
}
.audio-item {
  display: flex;
  align-items: center;
}
.el-divider {
  margin: 16px 0;
}
</style>
